import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useController, Control, FieldValues, Path } from 'react-hook-form';
import { Dropdown } from 'react-bootstrap';
import { useDropdownKeyboardNav } from '../../hooks/useDropdownKeyboardNav';

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface RHFSearchableSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/** Removes diacritics for accent-insensitive matching. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * RHF-compatible searchable dropdown using the same select2 visual pattern
 * as the existing InputCountry / InputContact components.
 * Stores a code (string value), displays a localized label.
 * Filters on both label and value, accent-insensitive.
 */
export function RHFSearchableSelect<T extends FieldValues>({
  name, control, label: fieldLabel, options, required, disabled, placeholder, className
}: RHFSearchableSelectProps<T>) {
  const { field, fieldState } = useController({ name, control });
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const selectableOptions = useMemo(
    () => options.filter(o => !o.disabled && o.value !== '__separator__'),
    [options]
  );

  const displayed = useMemo(() => {
    if (!search) return options; // Show full list with separators when not filtering
    const needle = normalize(search);
    return selectableOptions.filter(
      o => normalize(o.label).includes(needle) || normalize(o.value).includes(needle)
    );
  }, [search, options, selectableOptions]);

  // Selectable items from displayed (excludes separators)
  const selectableDisplayed = useMemo(
    () => displayed.filter(o => o.value !== '__separator__'),
    [displayed]
  );

  const selectedLabel = useMemo(() => {
    if (!field.value) return null;
    const found = selectableOptions.find(o => o.value === field.value);
    return found?.label ?? null;
  }, [field.value, selectableOptions]);

  const handleSelect = useCallback((opt: Option) => {
    field.onChange(opt.value);
  }, [field]);

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow);
    if (nextShow) setSearch('');
  };

  const handleClose = useCallback(() => {
    toggleRef.current?.click();
  }, []);

  const { highlightedIndex, handleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: selectableDisplayed.length,
      onSelect: (idx) => { handleSelect(selectableDisplayed[idx]); handleClose(); },
      isOpen,
      onClose: handleClose,
      idPrefix: `rhf-${name as string}`,
    });

  return (
    <div className={`fv-row mb-5 ${className || ''}`}>
      <label className={`form-label ${required ? 'required' : ''}`}>{fieldLabel}</label>
      <Dropdown onToggle={handleToggle}>
        <Dropdown.Toggle
          ref={toggleRef}
          variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0"
          disabled={disabled}
        >
          <span className="selection">
            <span className={`select2-selection select2-selection--single form-select ${fieldState.error ? 'is-invalid' : ''}`}>
              <span className="select2-selection__rendered" role="textbox">
                {selectedLabel ?? placeholder ?? ''}
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-search select2-search--dropdown">
              <input
                className="select2-search__field"
                type="text"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeDescendant}
                aria-expanded={isOpen}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </span>
            <span className="select2-results">
              <ul className="select2-results__options" id={listboxId} role="listbox">
                {displayed.filter(o => o.value !== '__separator__' || !search).length > 0
                  ? (() => {
                      let selectableIdx = -1;
                      return displayed.map((opt, idx) => {
                        if (opt.value === '__separator__') {
                          return <li key={`sep-${idx}`} className="select2-results__option" style={{ padding: '2px 8px', fontSize: '0.75rem', color: '#bbb', pointerEvents: 'none' }}>───</li>;
                        }
                        selectableIdx++;
                        const thisSelectableIdx = selectableIdx;
                        return (
                          <li
                            key={opt.value}
                            id={getOptionId(thisSelectableIdx)}
                            className={`select2-results__option select2-results__option--selectable ${opt.value === field.value ? 'select2-results__option--selected' : ''} ${thisSelectableIdx === highlightedIndex ? 'select2-results__option--highlighted' : ''}`}
                            role="option"
                            aria-selected={opt.value === field.value}
                          >
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => { handleSelect(opt); handleClose(); }}>
                              {opt.label}
                            </Dropdown.Item>
                          </li>
                        );
                      });
                    })()
                  : <li className="select2-results__option select2-results__message" role="alert">
                      {placeholder || 'No results'}
                    </li>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      {fieldState.error && (
        <div className="fv-plugins-message-container invalid-feedback d-block">
          {fieldState.error.message}
        </div>
      )}
    </div>
  );
}
